/**
 * The accessibility attributes a form control needs so its label, help text and error text
 * are announced together.
 *
 * @since 1.0.0
 */
export interface FieldControlAttributes {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': true | undefined;
}

/**
 * Builds the id of a field's help text.
 *
 * @since 1.0.0
 * @param controlId - the control's own id
 * @returns the help element id
 */
export function helpId(controlId: string): string {
    return `${controlId}-help`;
}

/**
 * Builds the id of a field's error text.
 *
 * @since 1.0.0
 * @param controlId - the control's own id
 * @returns the error element id
 */
export function errorId(controlId: string): string {
    return `${controlId}-error`;
}

/**
 * Builds the attributes to spread onto a form control so it points at whichever of its help
 * and error text is actually rendered.
 *
 * @since 1.0.0
 * @param controlId - the control's own id
 * @param error - the current error message, if the field has one
 * @param hasDescription - whether the field renders help text
 * @returns the attributes to spread onto the control
 */
export function fieldControlProps(
    controlId: string,
    error?: string | null,
    hasDescription = false,
): FieldControlAttributes {
    const described: string[] = [];

    if (hasDescription) {
        described.push(helpId(controlId));
    }

    if (error !== null && error !== undefined && error !== '') {
        described.push(errorId(controlId));
    }

    return {
        id: controlId,
        'aria-describedby': described.length === 0 ? undefined : described.join(' '),
        'aria-invalid': error === null || error === undefined || error === '' ? undefined : true,
    };
}
